import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import cn from "classnames";
import { Button, BUTTON_VARIANT } from "components/UI/Button";
import { COLOR_PALETTE } from "constants/colors.constants";
import { useOnClickOutside } from "hooks/useOnClickOutside.hook";
import { useTranslation } from "localization/localization";
import { AppDOM } from "utils/app.utils";
import { ColorPickerProps } from "./ColorPicker.types";
import s from "./ColorPicker.module.scss";

export const ColorPicker = ({ value, onChange }: ColorPickerProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [tempColor, setTempColor] = useState(value);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const savedColorRef = useRef(value);
  const canUndoRef = useRef(false);
  const [anchor, setAnchor] = useState({ top: 0, left: 0, width: 0, height: 0 });

  const handleOpen = () => {
    if (!canUndoRef.current) savedColorRef.current = value;
    setTempColor(value);
    setIsOpen(true);
  };

  const handleSelect = (color: string) => {
    setTempColor(color);
    onChange(color);
    canUndoRef.current = true;
    setIsOpen(false);
  };

  const handleCancel = () => {
    setTempColor(savedColorRef.current);
    onChange(savedColorRef.current);
    canUndoRef.current = false;
    setIsOpen(false);
  };

  useLayoutEffect(() => {
    if (!isOpen) return;

    const placeAnchor = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setAnchor({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
    };

    placeAnchor();
    window.addEventListener("resize", placeAnchor);
    window.addEventListener("scroll", placeAnchor, true);

    return () => {
      window.removeEventListener("resize", placeAnchor);
      window.removeEventListener("scroll", placeAnchor, true);
    };
  }, [isOpen]);

  useOnClickOutside({
    ref: containerRef,
    shadowRoot: AppDOM.shadowRootNode,
    excludeRefs: [dropdownRef],
    handler: () => setIsOpen(false),
  });

  return (
    <div className={s.wrapper} ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        className={s.trigger}
        style={{ backgroundColor: isOpen ? tempColor : value }}
        onClick={() => (isOpen ? setIsOpen(false) : handleOpen())}
        aria-label={t("Select color")}
        title={t("Select color")}
      />
      {isOpen &&
        createPortal(
          <div
            className={s.anchor}
            style={{ top: anchor.top, left: anchor.left, width: anchor.width, height: anchor.height }}
          >
            <div className={s.dropdown} ref={dropdownRef}>
              <span className={s.label}>{t("Color")}</span>
              <div className={s.palette}>
                {COLOR_PALETTE.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={cn(s.swatch, { [s.selected]: color === tempColor })}
                    style={{ backgroundColor: color }}
                    onClick={() => handleSelect(color)}
                    aria-label={color}
                  />
                ))}
              </div>
              <div className={s.actions}>
                <Button variant={BUTTON_VARIANT.SECONDARY} className={s.btn} onClick={handleCancel}>
                  {t("Cancel")}
                </Button>
              </div>
            </div>
          </div>,
          AppDOM.appContainer ?? document.body
        )}
    </div>
  );
};
